<template>
  <form
    class="d-discover-feed-search"
    @submit.stop.prevent="searchTextUpdated()"
  >
    <button
      class="hidden-submit-button"
      type="submit"
    />

    <p class="large padding-top-3em padding-bottom-3em border-bottom hidden-until-md">
      Browse popular genres
    </p>

    <DuiExpandableOptions
      ref="genresList"
      v-model="search.genre"
      class="space-top-2em hidden-until-md"
      store-load-key="DiscoverFeed/get-genre-options"
      @change="searchParamUpdated('genre')"
    />

    <div class="visible-until-sm">
      <div class="grid-flex grid-middle grid-no-response">
        <div class="grid-item">
          <DuiIconSearchFilters />
          <strong class="valign-middle">Search filters</strong>
        </div>
        <div class="grid-item-shrink">
          <a @click="toggleMoreFilters()">
            <span v-if="isMoreOptionsExpanded">Hide filters</span>
            <span v-if="!isMoreOptionsExpanded">Show all</span>
          </a>
        </div>
      </div>

      <DuiDropdown
        ref="genresDropdown"
        v-model="mobileGenre"
        class="space-top-2em"
        :disabled="isProcessing"
        label="Genre"
        name="genre"
        :nested="true"
        placeholder="Select genre"
        :search="true"
        store-load-key="DiscoverFeed/get-genre-options"
        @option-selected="mobileOptionSelected"
      />
    </div>

    <div
      class="more-filters"
      :class="{ 'expanded': isMoreOptionsExpanded }"
    >
      <div class="input-group space-top-2em">
        <input
          v-model="search.text"
          class="input-full"
          :disabled="isProcessing"
          placeholder="Search by keyword"
          type="text"
        >
        <DuiIconSearch class="space-right-1em" />
      </div>

      <DuiDropdown
        ref="timescaleDropdown"
        v-model="search.timescale"
        class="space-top-2em"
        :disabled="isProcessing"
        label="Sort by"
        :label-width="64"
        :options="timescaleOptions"
        name="timescale"
        @change="searchParamUpdated('timescale')"
      />

      <DuiCheckbox
        v-model="search.hideExplicitContent"
        class="space-top-2em"
        :disabled="isProcessing"
        label="Hide explicit content"
        name="hideExplicitContent"
        title="Hide explicit content"
        @change="searchParamUpdated('hideExplicitContent')"
      />
    </div>
  </form>
</template>

<script>
import Vue from 'vue';

import discoverSearchService from '@d-services/books-search-service/discover-search-service';
import DropdownOptionsHelper from '@d-helpers/dropdown-options-helper';
import genresService from '@d-services/genres-service/genres-service';
import { getErrorMessage } from '@d-shared/utils/errors';
import HistoryHelper from '@d-helpers/history-helper';
import SearchHelper from '@d-helpers/search-helper';
import UrlParamsHelper from '@d-helpers/url-params-helper';
import URLS from '@d-shared/urls/urls';

const ALL_GENRES_OPTION = {
  selected: true,
  text: 'All genres',
  value: 0,
};

let _textTagId = 0;

function _getGenreBySlug(genres, slug) {
  if (!genres || !genres.length) {
    return null;
  }

  const len = genres.length;
  let foundGenre = null;
  let i = 0;

  while (!foundGenre && (i < len)) {
    const genre = genres[i];

    if (genre.slug === slug) {
      foundGenre = genre;
    } else {
      foundGenre = _getGenreBySlug(genre.options, slug);
    }

    i++;
  }

  return foundGenre;
}

export default {
  data: () => ({
    isLoadingUrlParams: false,
    isMoreOptionsExpanded: false,
    isProcessing: false,
    mobileGenre: null,
    search: {
      genre: null,
      hideExplicitContent: false,
      text: null,
      timescale: null,
    },
    timescaleOptions: [
      {
        text: 'This week',
        value: 'week',
      },
      {
        text: 'This month',
        value: 'month',
      },
      {
        text: 'This year',
        value: 'year',
      },
      {
        text: 'All time',
        value: 'all',
      },
    ],
  }),
  computed: {
    genreOptions() {
      return this.$store.getters['DiscoverFeed/get-genre-options'];
    },
    storeSearchTags() {
      return this.$store.getters['DiscoverFeed/BooksSearch/get-all-search-tags'];
    },
  },
  watch: {
    storeSearchTags: {
      handler: function() {
        this.storeSearchTagsUpdated();
      },
      deep: true,
    },
    'search.genre': function(newGenre) {
      this.$store.dispatch('DiscoverFeed/setSelectedGenre', newGenre);
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    addSearchTag(searchTag, performSearch) {
      if (!performSearch || this.isLoadingUrlParams) {
        this.$store.dispatch('DiscoverFeed/BooksSearch/addSearchTag', searchTag);
        return;
      }

      this.$store.dispatch('DiscoverFeed/BooksSearch/addSearchTagAndSearch', searchTag);
    },

    expandGenre(genreName) {
      const genre = this.genreOptions.find(g => g.text === genreName);
      if (!genre) {
        return;
      }

      this.$set(genre, 'expanded', true);
    },

    getGenre() {
      // We ignore if the update was caused by synching
      // desktop and mobile Genres search elements
      const genreTag = this.storeSearchTags.find(t => t.parentKey === 'genres');
      if (genreTag && genreTag.value === this.search.genre.value) {
        return null;
      }

      return {
        key: 'genres',
        query: 'genre_id',
        tags: [
          {
            hidden: true,
            key: `genre-${ this.search.genre.value }`,
            option: this.search.genre,
            parentKey: 'genres',
            text: this.search.genre.text,
            value: this.search.genre.value,
          },
        ],
        value: this.search.genre.value ? [ this.search.genre.value ] : null,
      };
    },

    getPageTag(pageNumber) {
      return {
        hidden: true,
        key: 'page',
        page: true,
        text: `Page ${ pageNumber }`,
        value: (pageNumber !== 1) ? pageNumber : null,
      };
    },

    getSearchTag(key) {
      switch(key) {
        case 'hideExplicitContent':
          return {
            hidden: true,
            key: key,
            query: 'explicit_content',
            text: 'Explicit content',
            value: this.search[key] ? false : null,
          };
        case 'genre':
          return this.getGenre();
        case 'timescale':
          return this.getTimescaleTag(this.search[key] || null);
        default:
          return null;
      }
    },

    getSortByTag(sortByValue) {
      return {
        hidden: true,
        key: 'sortBy',
        order: true,
        value: sortByValue || null,
      };
    },

    getTimescaleTag(timescaleValue) {
      return {
        hidden: true,
        key: 'timescale',
        query: null,
        value: timescaleValue !== 'week' ? timescaleValue : null,
      };
    },

    async init() {
      await this.loadGenres();
      await this.loadUrlParams();
    },

    async loadGenres() {
      try {
        const genres = await genresService.getPopularGenres(true, true);
        genres.unshift(ALL_GENRES_OPTION);
        DropdownOptionsHelper.setOptionsHrefWithBaseUrl(genres, URLS.app.discover);
        this.$store.dispatch('DiscoverFeed/setAvailableGenres', genres);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }
    },

    async loadUrlParams() {
      this.$set(this, 'isLoadingUrlParams', true);
      let initialGenre = UrlParamsHelper.getParamAfter(`${ URLS.app.discover }/`);
      const queryParams = UrlParamsHelper.getParamsFromCurrentUrl();
      const paramKeys = Object.keys(queryParams);

      if (initialGenre) {
        initialGenre = _getGenreBySlug(this.genreOptions, initialGenre);
      }

      initialGenre = initialGenre || ALL_GENRES_OPTION;

      this.$set(this.search, 'genre', initialGenre);
      this.searchParamUpdated('genre', false);

      if (!paramKeys.includes('timescale')) {
        this.$set(this.search, 'timescale', 'week');
      }

      paramKeys.forEach(key => {
        const currentParams = queryParams[key];

        switch (key) {
          case 'term':
            this.$set(this.search, 'text', currentParams.join(' '));
            this.searchTextUpdated(false);
            break;
          case 'page':
            const pageTag = this.getPageTag(currentParams[0]);
            this.addSearchTag(pageTag, false);
            break;
          case 'sortBy':
            const sortByTag = this.getSortByTag(currentParams[0]);
            this.addSearchTag(sortByTag, false);
            break;
          case 'hideExplicitContent':
            this.$set(this.search, key, !currentParams[0]);
            this.searchParamUpdated(key, false);
            break;
          case 'timescale':
            this.$set(this.search, key, currentParams[0]);
            this.searchParamUpdated(key, false);
            break;
        }
      });

      if (initialGenre === ALL_GENRES_OPTION) {
        await this.$nextTick();
        this.expandGenre('Fiction');
      }

      await this.$nextTick();

      discoverSearchService.search();
      this.$set(this, 'isLoadingUrlParams', false);
    },

    mobileOptionSelected(option) {
      this.$set(this.search, 'genre', option);
      this.searchParamUpdated('genre');
    },

    setGenre(genre) {
      this.$refs.genresList.deselectAllOptions();
      this.$set(genre, 'selected', true);
      this.$set(this, 'mobileGenre', genre.value);
    },

    removeSearchTag(searchTag) {
      this.$store.dispatch('DiscoverFeed/BooksSearch/removeSearchTag', searchTag);
    },

    toggleMoreFilters() {
      this.$set(this, 'isMoreOptionsExpanded', !this.isMoreOptionsExpanded);
    },

    updateCheckboxFromTag(fieldName) {
      const tag = this.storeSearchTags.find(t => t.key === fieldName);
      if (!tag) {
        this.updateSearchFieldValue(fieldName, false);
        return;
      }

      this.updateSearchFieldValue(fieldName, true);
    },

    updateCurrentUrl() {
      let newUrl = URLS.app.discover;

      const genreTag = this.storeSearchTags.find(t => t.parentKey === 'genres');
      if (genreTag) {
        newUrl = `${ newUrl }/${ genreTag.option.slug }`;
      }

      const otherTags = this.storeSearchTags.filter(t => t.parentKey !== 'genres');
      const tags = {
        term: [],
      };
      otherTags.forEach(t => {
        if (t.isTerm === true) {
          tags.term.push(t.text);
          return;
        }

        tags[t.key] = t.value;
      });

      const searchParams = UrlParamsHelper.getSearchFromParams(tags);
      if (searchParams) {
        newUrl = `${ newUrl }?${ searchParams }`;
      }

      HistoryHelper.updateEntry(null, '', newUrl);
    },

    updateGenreFromTag() {
      const genreTag = this.storeSearchTags.find(t => t.parentKey === 'genres');
      if (genreTag) {
        if (genreTag.value !== this.mobileGenre) {
          this.$set(this, 'mobileGenre', genreTag.value);
        }
        return;
      }

      this.setGenre(ALL_GENRES_OPTION);
    },

    updateSearchFieldValue(fieldName, value) {
      if (this.search[fieldName] === value) {
        return;
      }

      this.$set(this.search, fieldName, value);
    },

    // Events
    searchParamUpdated(key, performSearch = true) {
      const searchTag = this.getSearchTag(key);
      if (!searchTag) {
        return;
      }

      if (searchTag.value === null) {
        this.removeSearchTag(searchTag);
        return;
      }

      this.addSearchTag(searchTag, performSearch);
    },

    searchTextUpdated(performSearch = true) {
      if (!this.search.text) {
        return;
      }

      const terms = SearchHelper.getTermsFromText(this.search.text);
      if (terms) {
        terms.forEach((term) => {
          this.addSearchTag({
            isTerm: true,
            key: `text-${ _textTagId++ }`,
            query: null,
            text: term,
            value: term,
          }, false);
        });

        this.search.text = '';

        if (performSearch) {
          discoverSearchService.search();
        }
      }
    },

    storeSearchTagsUpdated() {
      this.updateCheckboxFromTag('hideExplicitContent');
      this.updateGenreFromTag();
      this.updateCurrentUrl();
    },
    // END Events
  },
};
</script>
